// Versão: 1.01
// Data..: 2026-10-02
// Busca rápida global (Ctrl+K / Cmd+K): navega por páginas e localiza chamados
// por número ou assunto. Sem backend novo — consulta os endpoints existentes.
// v1.01: atalho /schedule rotulado como "Agendamentos" (alinhado ao menu).

import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TicketStatus, UserRole, type Ticket, type Paginated } from '@makweb/shared';
import { api } from '../lib/api.js';
import { useAuth } from '../context/AuthContext.js';

// Ação navegável mostrada na paleta.
interface PaletteItem {
  id: string;
  label: string;
  hint?: string;
  to: string;
  icon: string;
}

// Status ativos varridos para localizar chamados (volume baixo).
const SEARCH_STATUSES = [
  TicketStatus.OPEN,
  TicketStatus.TRIAGE,
  TicketStatus.IN_PROGRESS,
  TicketStatus.WAITING_CLIENT,
  TicketStatus.WAITING_THIRD_PARTY,
  TicketStatus.SCHEDULED,
  TicketStatus.RESOLVED,
];

export function CommandPalette() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStaff = user?.role === UserRole.ADMIN || user?.role === UserRole.AGENT;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Atalhos fixos de navegação (filtrados por papel).
  const shortcuts: PaletteItem[] = [
    { id: 'nav-new', label: 'Novo chamado', to: '/tickets/new', icon: '➕' },
    { id: 'nav-dash', label: 'Painel', to: '/', icon: '▤' },
    ...(isStaff ? [{ id: 'nav-central', label: 'Minha Central', to: '/central', icon: '🎯' }] : []),
    { id: 'nav-tickets', label: 'Chamados', to: '/tickets', icon: '🎫' },
    { id: 'nav-kb', label: 'Base de conhecimento', to: '/kb', icon: '📖' },
    ...(isStaff ? [{ id: 'nav-schedule', label: 'Agendamentos', to: '/schedule', icon: '🗓' }] : []),
    ...(isStaff ? [{ id: 'nav-admin', label: 'Administração', to: '/admin', icon: '⚙' }] : []),
  ];

  // Abre/fecha com Ctrl+K (ou Cmd+K no Mac); fecha com Esc.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Ao abrir, foca o campo e limpa o estado.
  useEffect(() => {
    if (open) {
      setQ(''); setTickets([]); setCursor(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // Busca chamados quando o texto tem 2+ caracteres (debounce simples).
  useEffect(() => {
    if (!open) return;
    const term = q.trim().toLowerCase();
    if (term.length < 2) { setTickets([]); return; }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const lists = await Promise.all(
          SEARCH_STATUSES.map((s) =>
            api.get<Paginated<Ticket>>(`/tickets?status=${s}&limit=100`).catch(() => ({ items: [] as Ticket[] })),
          ),
        );
        if (!alive) return;
        const all = lists.flatMap((l) => l.items);
        const seen = new Set<string>();
        const matched = all.filter((tk) => {
          if (seen.has(tk.id)) return false;
          seen.add(tk.id);
          return String(tk.number).includes(term) || tk.subject.toLowerCase().includes(term);
        }).slice(0, 8);
        setTickets(matched);
        setCursor(0);
      } catch {
        setTickets([]);
      }
    }, 220);
    return () => { alive = false; clearTimeout(t); };
  }, [q, open]);

  // Lista combinada: atalhos filtrados pelo texto + chamados encontrados.
  const term = q.trim().toLowerCase();
  const filteredShortcuts = term
    ? shortcuts.filter((s) => s.label.toLowerCase().includes(term))
    : shortcuts;
  const ticketItems: PaletteItem[] = tickets.map((t) => ({
    id: `tk-${t.id}`,
    label: `#${t.number} — ${t.subject}`,
    hint: 'Chamado',
    to: `/tickets/${t.id}`,
    icon: '🎫',
  }));
  const items = [...ticketItems, ...filteredShortcuts];

  const go = useCallback((to: string) => { setOpen(false); navigate(to); }, [navigate]);

  const onInputKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); const it = items[cursor]; if (it) go(it.to); }
  };

  if (!open) return null;

  return (
    <div
      onClick={() => setOpen(false)}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 50, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '12vh' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ width: 'min(560px, 92vw)', padding: 0, overflow: 'hidden', boxShadow: 'var(--shadow-lg)' }}
      >
        <input
          ref={inputRef}
          className="input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onInputKey}
          placeholder="Buscar chamado por número/assunto ou navegar..."
          style={{ border: 'none', borderRadius: 0, borderBottom: '1px solid var(--border)', fontSize: 15, padding: '14px 16px' }}
        />
        <div style={{ maxHeight: '52vh', overflowY: 'auto' }}>
          {items.length === 0 ? (
            <div className="text-dim text-sm" style={{ padding: 16 }}>
              {term.length >= 2 ? 'Nada encontrado.' : 'Digite para buscar. Use ↑ ↓ e Enter.'}
            </div>
          ) : (
            items.map((it, i) => (
              <div
                key={it.id}
                onMouseEnter={() => setCursor(i)}
                onClick={() => go(it.to)}
                className="flex items-center gap-3"
                style={{ padding: '10px 16px', cursor: 'pointer', background: i === cursor ? 'var(--surface-3)' : 'transparent' }}
              >
                <span style={{ fontSize: 16 }}>{it.icon}</span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.label}</span>
                {it.hint && <span className="text-dim text-xs">{it.hint}</span>}
              </div>
            ))
          )}
        </div>
        <div className="text-dim text-xs flex items-center justify-between" style={{ padding: '8px 16px', borderTop: '1px solid var(--border)' }}>
          <span>↑ ↓ navegar · Enter abrir · Esc fechar</span>
          <span>Ctrl/Cmd + K</span>
        </div>
      </div>
    </div>
  );
}
